import { MutationHookOptions } from '@apollo/client'
import { customerErrorHandling } from 'lib/hooks'
import {
  CustomerType,
  CustomerUpdateInput,
  useCustomerUpdateMutation,
} from 'lib/graphql/storeadmin'
import { Form, Input, Modal, ModalProps, showMessage } from 'components/common'
import { NameAndEmailForm } from 'components/customers'
import AllowDuplicateModal from './AllowDuplicateModal'
import { useState } from 'react'
import {
  DuplicateFieldsValue,
  getDuplicateErrorMessages,
} from 'pages/admin/customers/corporate/[id]/edit'

type FormValues = {
  allowDuplicate?: boolean
  id: string
  corporationName: string
  corporationNameKana: string
  firstName: string
  lastName: string
  email: string
  phone: string
  type?: string
}

export interface OverviewEditorModalProps
  extends Pick<ModalProps, 'visible' | 'onCancel'>,
    Pick<MutationHookOptions, 'refetchQueries'> {
  defaultValues?: FormValues
  type: 'personal' | 'corporate'
  onSuccess?: () => void | Promise<void>
  onErrorMessage?: (value: string[]) => void | Promise<void>
  onErrorAlert?: () => void
  onCancel?: () => void
}

export const OverviewEditorModal = ({
  defaultValues,
  visible,
  refetchQueries,
  type,
  onSuccess,
  onCancel,
  onErrorMessage,
  onErrorAlert,
}: OverviewEditorModalProps): JSX.Element => {
  const [form] = Form.useForm<FormValues>()
  const [updateCustomer, { loading, error }] = useCustomerUpdateMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  })

  // モーダルの表示・非表示
  const [showModal, setShowModal] = useState(false)
  // 重複可否を選択するモーダルのonOKの引数に渡すvaluesを一時的に保存するためのステート
  const [tmpValues, setTmpValues] = useState<CustomerUpdateInput>(null)
  // 重複エラーのフィールド名を日本語の項目名に変換した文字列を格納するステート
  const [duplicateErrorMessages, setDuplicateErrorMessages] =
    useState<string[]>(null)

  // 重複可否選択モーダルのOKボタンをクリックしたときの処理
  const handleClickAllowDuplicate = (
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    if (tmpValues) {
      onFinish({
        allowDuplicate: true,
        id: defaultValues.id,
        firstName: tmpValues.firstName,
        lastName: tmpValues.lastName,
        corporationName: tmpValues.corporationName,
        corporationNameKana: tmpValues.corporationNameKana,
        email: tmpValues.email,
        phone: tmpValues.phone,
        type: type,
      })
    } else {
      showMessage('error', 'エラーが発生しました。')
    }
    setShowModal(false)
  }

  const onFinish = async ({
    // 個人の時は重複可否をチェックしない
    allowDuplicate = type === 'personal' ? true : false,
    id,
    firstName,
    lastName,
    corporationName,
    corporationNameKana,
    email,
    phone,
  }: FormValues) => {
    const input = {
      firstName,
      lastName,
      corporationName,
      corporationNameKana,
      email,
      phone,
      type:
        type === 'personal' ? CustomerType.Personal : CustomerType.Corporate,
    }

    try {
      const { data } = await updateCustomer({
        variables: { allowDuplicate, id, input },
      })
      const userErrors = data?.customerUpdate?.customerUserErrors || []
      if (userErrors.length > 0) {
        // 重複エラーがある場合
        if (userErrors.some(({ code }) => code === 'DUPLICATE_DATA_EXISTS')) {
          // 重複エラーがある場合、重複可否を選択するモーダルを表示する
          setShowModal(true)

          // 編集モーダルを閉じる
          onCancel()

          // フォームの入力値を保存する
          // 重複可否モーダルで「登録」を実行した時に使用する
          setTmpValues(input)

          // 重複エラーのフィールド名を取得する
          const duplicateFields = userErrors
            .filter(({ code }) => code === 'DUPLICATE_DATA_EXISTS')
            .flatMap(({ field }) => field)

          // 重複可否モーダルに表示する重複した項目の入力値のオブジェクトを作成する
          const duplicateFieldsValues: DuplicateFieldsValue = {
            corporationName: input.corporationName,
            corporationNameKana: input.corporationNameKana,
            firstName: input.firstName,
            lastName: input.lastName,
            phone: input.phone,
          }

          // 重複エラーのフィールド名からエラーメッセージを生成して、ステートを更新
          setDuplicateErrorMessages(
            getDuplicateErrorMessages(duplicateFields, duplicateFieldsValues)
          )
        } else {
          const errorMessages = customerErrorHandling(userErrors)
          onErrorAlert()
          onErrorMessage(errorMessages)
          showMessage(
            'error',
            'エラーが発生しました。入力項目を確認してください。'
          )
        }
      } else if (error) {
        showMessage('error', 'データの更新時にサーバーでエラーが発生しました。')
      } else {
        showMessage('success', 'データの更新が完了しました。')
        onSuccess?.()
        setShowModal(false)
        setTmpValues(null)
      }
    } catch (e) {
      onErrorAlert()
      onErrorMessage([e.toString()])
      showMessage('error', 'データの更新時にサーバーでエラーが発生しました')
    }
  }

  return (
    <>
      <Modal
        title="お客様の概要を編集する"
        visible={visible}
        confirmLoading={loading}
        destroyOnClose
        width={700}
        okType="primary"
        okText="保存する"
        onOk={form.submit}
        okButtonProps={{ htmlType: 'submit' }}
        cancelText="キャンセル"
        onCancel={onCancel}
      >
        <Form
          form={form}
          component="form"
          requiredMark="optional"
          onFinish={onFinish}
        >
          <Form.Item name="id" initialValue={defaultValues?.id} hidden>
            <Input />
          </Form.Item>

          <NameAndEmailForm type={type} defaultValues={defaultValues} />
        </Form>
      </Modal>
      {/* 重複可否選択モーダル */}
      <AllowDuplicateModal
        errorMessages={duplicateErrorMessages}
        onCancel={() => {
          setShowModal(false)
          setTmpValues(null)
        }}
        onOk={handleClickAllowDuplicate}
        okText="更新"
        open={showModal}
        loading={loading}
      />
    </>
  )
}
